import { Injectable, signal } from '@angular/core';
import {
  ApplicationScheme,
  applicationSchemeToSlug,
  normalizeApplicationScheme,
} from '../../shared/constants/application-scheme';
import {
  WIZARD_BRANDING_METADATA,
  type WizardBrandingMetadata,
  type WizardBrandKey,
} from './wizard-branding.metadata';

@Injectable({ providedIn: 'root' })
export class WizardBrandingService {
  private readonly activeBrandingSignal = signal<WizardBrandingMetadata>(this.getInitialBranding());

  readonly activeBranding = this.activeBrandingSignal.asReadonly();

  constructor() {
    this.applyBrandingCssVariables(this.activeBrandingSignal());
  }

  private getInitialBranding(): WizardBrandingMetadata {
    if (typeof window === 'undefined') {
      return WIZARD_BRANDING_METADATA.default;
    }

    const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    return this.resolveBrandingFromUrl(path);
  }

  setActiveBrandingFromUrl(url: string): WizardBrandingMetadata {
    const metadata = this.resolveBrandingFromUrl(url);
    this.activeBrandingSignal.set(metadata);
    this.applyBrandingCssVariables(metadata);
    return metadata;
  }

  setActiveBrandingFromApplicationScheme(applicationScheme: string | null | undefined): WizardBrandingMetadata {
    const normalizedScheme = normalizeApplicationScheme(applicationScheme);
    const scheme = normalizedScheme === ApplicationScheme.OTHER
      ? ApplicationScheme.CNCS
      : normalizedScheme;
    const metadata = this.getBrandingByKey(applicationSchemeToSlug(scheme));
    this.activeBrandingSignal.set(metadata);
    this.applyBrandingCssVariables(metadata);
    return metadata;
  }

  resolveBrandingFromUrl(url: string): WizardBrandingMetadata {
    const redirectUrl = this.extractRedirectUrl(url);

    if (redirectUrl) {
      const redirectedBranding = this.resolveBrandingFromUrl(redirectUrl);
      if (redirectedBranding.key !== 'default') {
        return redirectedBranding;
      }
    }

    const path = url.split('?')[0].split('#')[0];
    const segments = path.split('/').filter(Boolean);
    const firstSegment = segments[0];

    if (!firstSegment) {
      return WIZARD_BRANDING_METADATA.default;
    }

    if (firstSegment === 'application') {
      return WIZARD_BRANDING_METADATA.cncs;
    }

    if (firstSegment === 'test' && segments[1]) {
      return this.getBrandingByKey(segments[1]);
    }

    if (this.isWizardBrandKey(firstSegment)) {
      return this.getBrandingByKey(firstSegment);
    }

    if (segments[1] === 'application') {
      return this.getBrandingByKey(firstSegment);
    }

    return WIZARD_BRANDING_METADATA.default;
  }

  private extractRedirectUrl(url: string): string | null {
    const query = url.split('?')[1]?.split('#')[0] ?? '';

    if (!query) {
      return null;
    }

    const params = new URLSearchParams(query);
    const redirectUrl = params.get('redirect_url');

    if (!redirectUrl) {
      return null;
    }

    try {
      return decodeURIComponent(redirectUrl);
    } catch {
      return redirectUrl;
    }
  }

  private getBrandingByKey(key: string): WizardBrandingMetadata {
    if (this.isWizardBrandKey(key)) {
      return WIZARD_BRANDING_METADATA[key];
    }

    return WIZARD_BRANDING_METADATA.default;
  }

  private isWizardBrandKey(key: string): key is Exclude<WizardBrandKey, 'default'> {
    return key in WIZARD_BRANDING_METADATA && key !== 'default';
  }

  private applyBrandingCssVariables(metadata: WizardBrandingMetadata): void {
    if (typeof document === 'undefined') {
      return;
    }

    const rootStyle = document.documentElement.style;
    const primaryRgb = this.hexToRgb(metadata.primaryColor);
    const primaryShade = this.adjustHexColor(metadata.primaryColor, -0.12);
    const primaryTint = this.adjustHexColor(metadata.primaryColor, 0.08);
    const primaryShadeRgb = this.hexToRgb(primaryShade);
    const primaryTintRgb = this.hexToRgb(primaryTint);

    rootStyle.setProperty('--app-primary', metadata.primaryColor);
    rootStyle.setProperty('--wizard-primary', metadata.primaryColor);
    rootStyle.setProperty('--wizard-primary-dark', metadata.primaryColorDark);
    rootStyle.setProperty('--ion-color-primary', metadata.primaryColor);
    rootStyle.setProperty('--ion-color-primary-rgb', primaryRgb);
    rootStyle.setProperty('--ion-color-primary-contrast', '#ffffff');
    rootStyle.setProperty('--ion-color-primary-contrast-rgb', '255, 255, 255');
    rootStyle.setProperty('--ion-color-primary-shade', primaryShade);
    rootStyle.setProperty('--ion-color-primary-shade-rgb', primaryShadeRgb);
    rootStyle.setProperty('--ion-color-primary-tint', primaryTint);
    rootStyle.setProperty('--ion-color-primary-tint-rgb', primaryTintRgb);
  }

  private hexToRgb(hex: string): string {
    const normalized = hex.replace('#', '');
    const safeHex = normalized.length === 3
      ? normalized.split('').map((value) => `${value}${value}`).join('')
      : normalized.padStart(6, '0').slice(0, 6);

    const red = Number.parseInt(safeHex.slice(0, 2), 16);
    const green = Number.parseInt(safeHex.slice(2, 4), 16);
    const blue = Number.parseInt(safeHex.slice(4, 6), 16);

    return `${red}, ${green}, ${blue}`;
  }

  private adjustHexColor(hex: string, amount: number): string {
    const normalized = hex.replace('#', '');
    const safeHex = normalized.length === 3
      ? normalized.split('').map((value) => `${value}${value}`).join('')
      : normalized.padStart(6, '0').slice(0, 6);

    const channels = [0, 2, 4].map((index) => Number.parseInt(safeHex.slice(index, index + 2), 16));
    const adjusted = channels.map((channel) => {
      const next = amount >= 0
        ? channel + (255 - channel) * amount
        : channel * (1 + amount);
      return Math.max(0, Math.min(255, Math.round(next)));
    });

    return `#${adjusted.map((value) => value.toString(16).padStart(2, '0')).join('')}`;
  }
}
